<script setup>
import { ref, onMounted } from 'vue';
import { Head } from '@inertiajs/vue3';
import axios from 'axios';

// State
const keywords = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const originalData = ref(null);

// Modal State
const errorModalContent = ref([]);
const modalType = ref('error');
const deleteKeywordItem = ref(null);
const isDeleting = ref(false);
const deleteConfirmedCheck = ref(false);

// Form state
const form = ref({
    id: null,
    keyword: '',
    url: '',
    description: ''
});

// Methods
const loadKeywords = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/auto-link-keywords/get');
        keywords.value = response.data;
    } catch (error) {
        console.error('Error loading keywords:', error);
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    form.value = { id: null, keyword: '', url: '', description: '' };
    isEditing.value = false;
    originalData.value = null;
};

const editKeyword = async (id) => {
    try {
        const response = await axios.get(`/admin/auto-link-keywords/${id}`);
        if (response.data.status === 'success') {
            const kw = response.data.data;
            const formData = {
                id: kw.hash_id,
                keyword: kw.keyword,
                url: kw.url,
                description: kw.description || ''
            };
            form.value = { ...formData };
            originalData.value = JSON.parse(JSON.stringify(formData));
            isEditing.value = true;
        }
    } catch (error) {
        showError('Gagal memuat data');
    }
};

const showError = (message, type = 'error') => {
    errorModalContent.value = Array.isArray(message) ? message : [message];
    modalType.value = type;
    const modal = new bootstrap.Modal(document.getElementById('errorModal'));
    modal.show();
};

const submitForm = async () => {
    saving.value = true;
    
    // Slight delay for UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Client-side Validation
    const errors = [];
    if (!form.value.keyword || form.value.keyword.trim() === '') {
        errors.push('Keyword wajib diisi.');
    }
    if (!form.value.url || form.value.url.trim() === '') {
        errors.push('URL wajib diisi.');
    }
    
    if (errors.length > 0) {
        saving.value = false;
        showError(errors);
        return;
    }

    // Check for changes if editing
    if (isEditing.value && originalData.value) {
        const currentData = JSON.stringify(form.value);
        const oldData = JSON.stringify(originalData.value);
        
        if (currentData === oldData) {
            saving.value = false;
            showError(['Tidak ada perubahan data yang disimpan.'], 'warning');
            return;
        }
    }
    
    let url = '/admin/auto-link-keywords';
    const data = { ...form.value };
    
    if (isEditing.value) {
        data._method = 'PUT';
        url = `/admin/auto-link-keywords/${form.value.id}`;
    }
    
    try {
        const response = await axios.post(url, data);
        if (response.data.status === 'success') {
            resetForm();
            loadKeywords();
        } else if (response.data.status === 'info') {
            showError(response.data.message, 'info');
        }
    } catch (error) {
        if (error.response?.data?.messages) {
            showError(error.response.data.messages);
        } else {
            showError('Terjadi kesalahan saat menyimpan data.');
            console.error(error);
        }
    } finally {
        saving.value = false;
    }
};

const toggleActive = async (keyword) => {
    try {
        const response = await axios.post(`/admin/auto-link-keywords/${keyword.hash_id}/toggle-active`);
        if (response.data.status === 'success') {
            loadKeywords();
        }
    } catch (error) {
        showError('Gagal mengubah status');
    }
};

const deleteKeyword = (keyword) => {
    deleteKeywordItem.value = keyword;
    deleteConfirmedCheck.value = false; // Reset checkbox
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const confirmDeleteAction = async () => {
    if (!deleteKeywordItem.value || !deleteConfirmedCheck.value) return;
    
    isDeleting.value = true;
    try {
        const response = await axios.delete(`/admin/auto-link-keywords/${deleteKeywordItem.value.hash_id}`);
        if (response.data.status === 'success') {
            loadKeywords();
            // Hide Modal
            const modalEl = document.getElementById('deleteModal');
            const modal = bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();
        }
    } catch (error) {
        showError('Gagal menghapus keyword');
    } finally {
        isDeleting.value = false;
        deleteKeywordItem.value = null;
    }
};

onMounted(() => {
    loadKeywords();
});
</script>

<template>
    <Head title="Auto Link Keywords" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Auto Link Keywords</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><a href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></a></li>
                                <li class="breadcrumb-item active" aria-current="page">Auto Link Keywords</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Panel -->
                <div class="col-xl-4 col-12">
                    <div class="box">
                        <form @submit.prevent="submitForm">
                            <div class="box-header with-border">
                                <h4 class="box-title">
                                    <i class="ti-link me-2"></i>{{ isEditing ? 'Edit' : 'Tambah' }} Keyword
                                </h4>
                            </div>
                            <div class="box-body">
                                <div class="form-group mb-3">
                                    <label class="form-label">Keyword <span class="text-danger">*</span></label>
                                    <input type="text" v-model="form.keyword" class="form-control" 
                                        placeholder="Contoh: rental mobil">
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label class="form-label">URL <span class="text-danger">*</span></label>
                                    <input type="url" v-model="form.url" class="form-control" 
                                        placeholder="https://example.com/page">
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label class="form-label">Deskripsi</label>
                                    <textarea v-model="form.description" class="form-control" rows="2"
                                        placeholder="Keterangan opsional"></textarea>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button v-if="isEditing" type="button" class="btn btn-warning me-2" @click="resetForm">
                                    <i class="ti-trash me-2"></i> Batal
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="ti-save-alt me-2"></i>
                                    {{ saving ? 'Menyimpan...' : (isEditing ? 'Simpan Perubahan' : 'Simpan') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                
                <!-- Table Panel -->
                <div class="col-xl-8 col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title"><i class="ti-view-list me-2"></i>Daftar Keywords</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Keyword</th>
                                            <th>URL</th>
                                            <th>Status</th>
                                            <th>Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading">
                                            <td colspan="5" class="text-center p-4">
                                                <div class="spinner-border text-primary"></div>
                                            </td>
                                        </tr>
                                        <tr v-else-if="keywords.length === 0">
                                            <td colspan="5" class="text-center text-muted p-4">
                                                Belum ada keyword
                                            </td>
                                        </tr>
                                        <tr v-else v-for="(kw, index) in keywords" :key="kw.id">
                                            <td>{{ index + 1 }}</td>
                                            <td>
                                                <strong>{{ kw.keyword }}</strong>
                                                <div v-if="kw.description" class="small text-muted">{{ kw.description }}</div>
                                            </td>
                                            <td>
                                                <a :href="kw.url" target="_blank" class="text-primary small">
                                                    {{ kw.url.length > 40 ? kw.url.substring(0, 40) + '...' : kw.url }}
                                                </a>
                                            </td>
                                            <td>
                                                <button type="button" 
                                                    :class="['btn', 'btn-sm', 'btn-toggle', kw.is_active ? 'active' : '']"
                                                    @click="toggleActive(kw)">
                                                    <span class="handle"></span>
                                                </button>
                                            </td>
                                            <td>
                                                <div class="btn-group">
                                                    <button class="btn btn-primary btn-sm" @click="editKeyword(kw.hash_id)" title="Edit"><i class="fa fa-edit"></i></button>
                                                    <button class="btn btn-danger btn-sm" @click="deleteKeyword(kw)" title="Hapus"><i class="fa fa-trash"></i></button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>

    <!-- Error/Warning Modal -->
    <div class="modal fade" id="errorModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                    <h5 class="modal-title">
                        <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                        {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                    </h5>
                    <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                    <p class="mb-3" v-else>Informasi sistem:</p>
                    <ul class="list-group list-group-flush">
                        <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(error, index) in errorModalContent" :key="index">
                            <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ error }}
                        </li>
                    </ul>
                </div>
                <div class="modal-footer text-end">
                    <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header bg-danger text-white">
                    <h5 class="modal-title"><i class="ti-trash me-2"></i>Konfirmasi Hapus</h5>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <p>Apakah Anda yakin ingin menghapus keyword:</p>
                    <p class="fw-bold text-primary">"{{ deleteKeywordItem?.keyword }}"</p>
                    <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                    
                    <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                        <input class="form-check-input" type="checkbox" id="confirmDeleteCheck" v-model="deleteConfirmedCheck">
                        <label class="form-check-label text-dark" for="confirmDeleteCheck">
                            Saya mengerti dan yakin ingin menghapus data ini.
                        </label>
                    </div>
                </div>
                <div class="modal-footer text-end">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                    <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck || isDeleting" @click="confirmDeleteAction">
                        <span v-if="isDeleting" class="spinner-border spinner-border-sm me-1"></span>
                        <i v-else class="ti-trash me-2"></i>{{ isDeleting ? 'Menghapus...' : 'Ya, Hapus' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
